import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
const SuppliersContext = createContext();

export const useSuppliers = () => {
  const context = useContext(SuppliersContext);
  if (!context) {
    throw new Error("useSuppliers must be used within a SuppliersProvider");
  }
  return context;
};

export const SuppliersProvider = ({ children }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [goods, setGoods] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classifiedPurchaseOrders, setClassifiedPurchaseOrders] = useState([]);
  const [loadingClassified, setLoadingClassified] = useState(true);
  const [errorClassified, setErrorClassified] = useState(null);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(
          "https://plusback.dims-ai.com/api/purchase-orders/suppliers-api"
        );
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // The backend returns an array of objects, each with a "data" key containing suppliers
        const allSuppliers = response.data.flatMap((item) => item.data || []);
        setSuppliers(allSuppliers);
        setError(null); // Clear any previous error
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        setError(
          "Failed to fetch suppliers. Please check if the backend server is running and the API endpoints are configured correctly."
        );
        setSuppliers([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    const fetchClassifiedPurchaseOrders = async () => {
      try {
        const endpoints = [
          "https://plusback.dims-ai.com/api/purchase-orders/classify-purchase-orders-api-0",
          "https://plusback.dims-ai.com/api/purchase-orders/classify-purchase-orders-api-1",
          "https://plusback.dims-ai.com/api/purchase-orders/classify-purchase-orders-api-2",
        ];
        const responses = await Promise.all(
          endpoints.map((endpoint) => axios.get(endpoint))
        );

        // Combine data from all responses
        const combinedData = responses.flatMap((response) => response.data);

        // Group by supplier
        const groupedBySupplier = {};
        combinedData.forEach((item) => {
          const supplier = item.supplier;
          if (supplier && supplier.id) {
            if (!groupedBySupplier[supplier.id]) {
              groupedBySupplier[supplier.id] = {
                id: supplier.id,
                name: supplier.name,
                total: 0,
                orders: [],
              };
            }
            groupedBySupplier[supplier.id].total += supplier.total || 0;
            groupedBySupplier[supplier.id].orders.push(...supplier.orders);
          }
        });

        const classifiedArray = Object.values(groupedBySupplier);
        setClassifiedPurchaseOrders(classifiedArray);
        setErrorClassified(null);
      } catch (err) {
        console.error("Error fetching classified purchase orders:", err);
        setErrorClassified("Failed to fetch classified purchase orders.");
        setClassifiedPurchaseOrders([]);
      } finally {
        setLoadingClassified(false);
      }
    };

    const fetchGoods = async () => {
      try {
        const response = await axios.get(
          "https://plusback.dims-ai.com/api/purchase-orders/good-api"
        );
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // The backend returns an array of objects, each with a "data" key containing suppliers
        const allGoods = response.data.flatMap((item) => item.data || []);
        setGoods(allGoods);
        setError(null); // Clear any previous error
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        setError(
          "Failed to fetch suppliers. Please check if the backend server is running and the API endpoints are configured correctly."
        );
        setGoods([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchGoods();

    fetchSuppliers();
    fetchClassifiedPurchaseOrders();
    fetchGoods();
  }, []);

  return (
    <SuppliersContext.Provider
      value={{
        suppliers,
        goods,
        loading,
        error,
        classifiedPurchaseOrders,
        loadingClassified,
        errorClassified,
      }}>
      {children}
    </SuppliersContext.Provider>
  );
};
